'use client'

import { createContext, useContext, useMemo } from 'react'
import { useMachine } from '@xstate/react'
import { channelListMachine } from './machines/channelListMachine'
import type { CrmChannel } from './types'

export type ChannelListMachineApi = ReturnType<typeof useMachine<typeof channelListMachine>>

const ChannelListMachineContext = createContext<ChannelListMachineApi | null>(null)

export function ChannelListMachineProvider({ children }: { children: React.ReactNode }) {
  const api = useMachine(channelListMachine)
  return (
    <ChannelListMachineContext.Provider value={api}>{children}</ChannelListMachineContext.Provider>
  )
}

export function useChannelListMachine() {
  const ctx = useContext(ChannelListMachineContext)
  if (!ctx) {
    throw new Error('useChannelListMachine must be used within ChannelListMachineProvider')
  }
  return ctx
}

export function useChannelListMachineOptional() {
  return useContext(ChannelListMachineContext)
}

export function useCrmChannelActions() {
  const machineApi = useChannelListMachine()
  const send = machineApi[1]

  return useMemo(() => ({
    setChannels: (channels: CrmChannel[]) => send({ type: 'SET_CHANNELS', channels }),
    addChannel: (channel: CrmChannel) => send({ type: 'ADD_CHANNEL', channel }),
    updateChannel: (id: string, updates: Partial<CrmChannel>) =>
      send({ type: 'UPDATE_CHANNEL', id, updates }),
    removeChannel: (id: string) => send({ type: 'REMOVE_CHANNEL', id }),
    setLoading: (loading: boolean) => send({ type: 'SET_LOADING', loading }),
  }), [send])
}

/**
 * Adapter: returns store-compatible interface.
 */
export function useChannelListStoreCompat() {
  const machineApi = useChannelListMachine()
  return useMemo(() => {
    const [state, send] = machineApi
    const channels = state.context.channels
    const isLoading = state.context.isLoading

    const getChannelById = (id: string) => channels.find((c) => c.id === id)
    const getChannelsByType = (type: string) => channels.filter((c) => c.type === type)
    const getChannelsByQueue = (queueId: string) => channels.filter((c) => c.queueId === queueId)
    const getActiveChannels = () => channels.filter((c) => c.status === 'active')

    return {
      channels,
      isLoading,
      setChannels: (c: CrmChannel[]) => send({ type: 'SET_CHANNELS', channels: c }),
      addChannel: (channel: CrmChannel) => send({ type: 'ADD_CHANNEL', channel }),
      updateChannel: (id: string, updates: Partial<CrmChannel>) =>
        send({ type: 'UPDATE_CHANNEL', id, updates }),
      removeChannel: (id: string) => send({ type: 'REMOVE_CHANNEL', id }),
      setLoading: (loading: boolean) => send({ type: 'SET_LOADING', loading }),
      getChannelById,
      getChannelsByType,
      getChannelsByQueue,
      getActiveChannels,
    }
  }, [machineApi])
}
